import { Alert, Box, ColumnLayout, FormField, Link, Select, SelectProps, SpaceBetween, Table, Toggle } from '@amzn/awsui-components-core';
import Input from '@amzn/awsui-components-core/input';
import { FormikProps, getIn } from 'formik';
import React from 'react';
import { useAppContext } from 'src/components/context/AppContextProvider';
import {
  labelInputToLabelDropdownOptions,
  labelInputToSelectDropdown,
  selectDropdownToLabel
} from 'src/components/generic-components/LabelDropdownUtility';
import { ApplicationFormValues } from '../../AdminModels';
import { NonProductionEnvironmentsConfig } from './LinksAndTags';

const QUICKSIGHT_CONSOLE_URL_PATTERN = /quicksight\.aws\.amazon\.com\/sn\/account\/[^/]+\/dashboards\//;
const QUICKSIGHT_EMBED_URL_PATTERN = /quicksight\.aws\.amazon\.com\/sn\/account\/[^/]+\/embed\/share\//;
const NON_EMBEDDABLE_WARNING = 'This looks like a QuickSight console URL, not an embed URL. Embedding may not work.';

export const isNonEmbeddableQuickSightUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  return QUICKSIGHT_CONSOLE_URL_PATTERN.test(url) && !QUICKSIGHT_EMBED_URL_PATTERN.test(url);
};

interface ApplicationLinksConfigProps {
  formik: FormikProps<ApplicationFormValues>;
}

const ApplicationLinksConfig: React.FC<ApplicationLinksConfigProps> = ({ formik }) => {
  const appContext = useAppContext();

  const isQuickSight = formik.values.applicationDetails?.toolUI?.label === 'Quicksight';
  const isEmbeddingEnabled = formik.values.applicationDetails?.embeddingEnabled || false;

  return (
    <SpaceBetween size="m" direction="vertical">
      <ColumnLayout columns={2}>
        <FormField
          label="Product UI"
          description="Select the product UI framework for this app"
          errorText={getIn(formik.touched, 'applicationDetails.toolUI') && getIn(formik.errors, 'applicationDetails.toolUI.label')}
        >
          <Select
            placeholder="Choose product UI"
            selectedOption={labelInputToSelectDropdown(formik.values.applicationDetails?.toolUI)}
            onChange={({ detail }) => {
              formik.setFieldValue('applicationDetails.toolUI', selectDropdownToLabel(detail.selectedOption, 'ToolUI'));
            }}
            onBlur={() => {
              formik.setFieldTouched('applicationDetails.toolUI', true);
            }}
            options={labelInputToLabelDropdownOptions(appContext.listApplicationDropdowns?.listToolUI)}
            filteringType="auto"
            selectedAriaLabel="Selected"
          />
        </FormField>

        <FormField label="Embedding Enabled" description="Allow this app to be embedded within FinSuite">
          <Toggle
            checked={formik.values.applicationDetails?.embeddingEnabled || false}
            onChange={({ detail }) => {
              formik.setFieldValue('applicationDetails.embeddingEnabled', detail.checked);
            }}
          >
            {formik.values.applicationDetails?.embeddingEnabled ? 'Enabled' : 'Disabled'}
          </Toggle>
        </FormField>
      </ColumnLayout>

      {isEmbeddingEnabled && isQuickSight && (
        <Alert type="info" header="QuickSight Embedding Setup">
          <SpaceBetween size="s">
            <Box>
              To embed a QuickSight dashboard, use the <strong>shareable embed link</strong> (not the browser URL). In QuickSight, open the dashboard,
              click <strong>Share &rarr; Copy embed link</strong>, and paste it below.
            </Box>
            <Table
              variant="embedded"
              columnDefinitions={[
                { id: 'type', header: 'Type', width: 120, cell: (item) => <strong>{item.type}</strong> },
                {
                  id: 'pattern',
                  header: 'URL Pattern',
                  cell: (item) => <code>{item.pattern}</code>
                },
                {
                  id: 'embeddable',
                  header: 'Embeddable?',
                  width: 120,
                  cell: (item) => <Box color={item.embeddable ? 'text-status-success' : 'text-status-error'}>{item.embeddable ? 'Yes' : 'No'}</Box>
                }
              ]}
              items={[
                { type: 'Console URL', pattern: '/sn/account/{alias}/dashboards/{id}', embeddable: false },
                { type: 'Embed URL', pattern: '/sn/account/{alias}/embed/share/accounts/{accountId}/dashboards/{id}', embeddable: true }
              ]}
            />
            <Box>
              The QuickSight administrator must also allowlist FinSuite's domain (<code>finsuite.device.finance.amazon.dev</code>) under{' '}
              <strong>Manage QuickSight &rarr; Domains and Embedding</strong>.{' '}
              <Link external href="https://docs.aws.amazon.com/quicksight/latest/user/embedding-with-iframe.html">
                Learn more
              </Link>
            </Box>
          </SpaceBetween>
        </Alert>
      )}

      <FormField
        label={
          <>
            Production Link<i> - optional</i>
          </>
        }
        description="The production URL used when embedding or linking to this application only in FinSuite Prod"
        constraintText="Include http:// or https:// in the URL"
        errorText={getIn(formik.touched, 'applicationDetails.applicationLink') && getIn(formik.errors, 'applicationDetails.applicationLink')}
        warningText={
          isQuickSight && isEmbeddingEnabled && isNonEmbeddableQuickSightUrl(formik.values.applicationDetails?.applicationLink)
            ? NON_EMBEDDABLE_WARNING
            : undefined
        }
        stretch={true}
      >
        <Input
          placeholder="Paste production URL"
          onChange={({ detail }) => {
            formik.setFieldValue('applicationDetails.applicationLink', detail.value);
          }}
          onBlur={() => {
            formik.setFieldTouched('applicationDetails.applicationLink', true);
          }}
          value={formik.values.applicationDetails?.applicationLink || ''}
        />
      </FormField>

      <NonProductionEnvironmentsConfig formik={formik} showEmbedWarnings={isQuickSight && isEmbeddingEnabled} />
    </SpaceBetween>
  );
};

export default ApplicationLinksConfig;
